import React, { lazy, Suspense, useMemo, useState } from "react";
import TaxManagementSection from "./TaxManagementSection";
import { Router, Route, Redirect, Switch } from "react-router-dom";
import GlobalAlerts from "./components/GlobalAlerts";
import AppToaster from "./components/AppToaster";
import { history } from "./helpers";
import PrivateRoute from "./PrivateRoute";
import enTranslations from "./locales/en.json";
import { IntlProvider } from "react-intl";
import PageLoadingOverlay from "./components/common/PageLoadingOverlay";
import "./App.scss";

const LoginPage = lazy(() => import("./components/auth").then((m) => ({ default: m.LoginPage })));
const SamaiHomePage = lazy(() => import("./Samai").then((m) => ({ default: m.default })));
const PrivacyPolicyPage = lazy(() => import("./Samai/PrivacyPolicyPage"));
const TermsAndConditionsPage = lazy(() => import("./Samai/TermsAndConditionsPage"));
const SignUpPage = lazy(() => import("./components/auth/SignUp/SignUpPage"));
const ForgotPasswordPage = lazy(
  () => import("./components/auth/ForgotPassword/ForgotPasswordPage")
);
const ForgotPasswordOtpPage = lazy(
  () => import("./components/auth/ForgotPasswordOtp/ForgotPasswordOtpPage")
);
const ResetPasswordPage = lazy(() => import("./components/auth/ResetPassword/ResetPasswordPage"));
const PasswordResetSuccessPage = lazy(
  () => import("./components/auth/PasswordResetSuccess/PasswordResetSuccessPage")
);
const InviteSetPasswordPage = lazy(
  () => import("./components/auth/InviteSetPassword/InviteSetPasswordPage")
);
const InviteExpiredPage = lazy(() => import("./components/auth/InviteStatus/InviteExpiredPage"));
const InviteInvalidPage = lazy(() => import("./components/auth/InviteStatus/InviteInvalidPage"));
const InviteSuccessPage = lazy(() => import("./components/auth/InviteStatus/InviteSuccessPage"));
const VerificationCodePage = lazy(
  () => import("./components/auth/VerificationCode/VerificationCodePage")
);
const CreatePasswordPage = lazy(
  () => import("./components/auth/CreatePassword/CreatePasswordPage")
);
const BusinessLinkCodePage = lazy(
  () => import("./components/auth/BusinessLinkCode/BusinessLinkCodePage")
);

const authSuspenseFallback = <PageLoadingOverlay />;

export default function App() {
  const [locale] = useState("en");
  const messages = useMemo(() => ({ en: enTranslations }), []);

  return (
    <div>
      <div>
        <div>
          <IntlProvider locale={locale} messages={messages[locale]}>
            <div className="App">
              <GlobalAlerts />
              <AppToaster />
              <Router history={history}>
                <Switch>
                  <Route
                    path="/login"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <LoginPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/HomePage"
                    render={(props) => (
                      <Suspense fallback={authSuspenseFallback}>
                        <SamaiHomePage props={props} />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/privacy-policy"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <PrivacyPolicyPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/terms-and-conditions"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <TermsAndConditionsPage />
                      </Suspense>
                    )}
                  />

                  <Route
                    path="/signup"
                    exact
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <SignUpPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/signup/verify"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <VerificationCodePage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/signup/create-password"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <CreatePasswordPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/signup/business-link"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <BusinessLinkCodePage />
                      </Suspense>
                    )}
                  />

                  <Redirect from="/TaxManagementSignUp" to="/signup" />
                  <Redirect from="/SignUpVerify" to="/signup/verify" />
                  <Redirect from="/SignUpVerification" to="/signup/create-password" />
                  <Redirect from="/SignUpReferalCode" to="/signup/business-link" />

                  <Route
                    path="/forgot-password"
                    exact
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <ForgotPasswordPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/forgot-password/otp"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <ForgotPasswordOtpPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/forgot-password/reset"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <ResetPasswordPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/forgot-password/success"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <PasswordResetSuccessPage />
                      </Suspense>
                    )}
                  />

                  <Route
                    path="/invite/set-password"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <InviteSetPasswordPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/invite/expired"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <InviteExpiredPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/invite/invalid"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <InviteInvalidPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/invite/success"
                    render={() => (
                      <Suspense fallback={authSuspenseFallback}>
                        <InviteSuccessPage />
                      </Suspense>
                    )}
                  />

                  <Redirect from="/TaxManagementForgotPassword" to="/forgot-password" />
                  <Redirect from="/EnterOtpForgotPassword" to="/forgot-password/otp" />
                  <Redirect from="/ResetForgotPassword" to="/forgot-password/reset" />

                  <PrivateRoute component={TaxManagementSection} />
                </Switch>
              </Router>
            </div>
          </IntlProvider>
        </div>
      </div>
    </div>
  );
}
